"use client";

import React from "react";
import { PhoneCall, ArrowRight, ShieldAlert, Clock, MapPin } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
export default function EmergencyCta() {
  const shouldReduceMotion = useReducedMotion();
  const trustHighlights = [{
    icon: Clock,
    label: "24/7 Live dispatch",
    desc: "No call centers"
  }, {
    icon: MapPin,
    label: "MD, DC & Northern VA",
    desc: "Fast on-site arrival"
  }, {
    icon: ShieldAlert,
    label: "Direct insurance billing",
    desc: "Zero hassle claims"
  }];
  return <section id="emergencycalltoaction" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-20 border-y border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative rounded-2xl bg-card text-card-foreground border border-border shadow-lg p-8 sm:p-10 md:p-12 overflow-hidden">
          {/* Subtle background decoration accentuating emergency readiness */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 md:gap-10">
            {/* Left Content Column */}
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-4">
                <Badge variant="outline" className="rounded-full px-3 py-1 font-medium text-xs bg-muted text-foreground border-border flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive" />
                  </span>
                  Emergency response team active
                </Badge>
              </div>

              <h2 className="font-default text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                Need emergency disaster restoration?
              </h2>

              <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
                Local crews are on standby 24/7 across Maryland, DC, and Northern Virginia to stop ongoing damage and coordinate directly with your insurance.
              </p>

              {/* Trust badges */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-border">
                {trustHighlights.map((item, index) => {
                const Icon = item.icon;
                return <div key={item.label} data-index={index} className="flex items-center gap-3">
                      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
                        <Icon className="size-4" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground">
                          {item.label}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {item.desc}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
            </div>

            {/* Right Action Column */}
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3 shrink-0 justify-center w-full lg:w-auto">
              <Button asChild size="lg" className="h-12 px-6 rounded-lg font-semibold shadow-md bg-primary text-primary-foreground hover:bg-primary/90 flex items-center justify-center gap-2.5 transition-[transform,box-shadow]">
                <a href="tel:8554356631">
                  <PhoneCall className="size-5" />
                  <span>Call 855-435-6631</span>
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="h-12 px-6 rounded-lg font-semibold bg-background hover:bg-muted text-foreground border-border flex items-center justify-center gap-2 transition-colors">
                <Link to="https://clienthub.getjobber.com/hubs/476c5fe2-f16a-4108-ad33-1763e02c3dad/public/requests/4972587/new" newTab>
                  <span>Request Service</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}